import React, { useEffect, useState } from "react";
import {
  GetAntrophoGraphData,
  GraphData,
} from "src/modules/fithub-shared/anthropometry/antrophometry";
import Bones from "../components/bones";
import Folds from "../components/folds";

import moment from "moment";
import ReactApexChart from "react-apexcharts";
import { BiCopy } from "react-icons/bi";
import { MdCircle, MdDelete, MdHeight, MdMonitorWeight } from "react-icons/md";
import { Audio } from "react-loader-spinner";
import Inputfield from "src/components/inputfield";
import { useStudent } from "src/contexts/student";
import { useDuplicateAssessment } from "src/modules/assessment/caching/queries";
import { AssessmentClass } from "src/modules/fithub-shared/anthropometry/assessment";
import { BonesList } from "src/modules/fithub-shared/anthropometry/bones";
import { SkinFoldsList } from "src/modules/fithub-shared/anthropometry/folds";
import { PerimetryList } from "src/modules/fithub-shared/anthropometry/perimetry";
import Perimetry from "../components/perimetry";
import { useAssessmentContext } from "../context/assessmentContext";
import useLoadingButton from "../useLoadingButton";

const DonutChart: React.FC<{
  data: GraphData;
}> = ({ data }) => {
  const colors4: string[] = ["#FF004F", "#00C372", "#175ddf"];
  const labels4 = ["Gordura (Kg)", "Residual (Kg)", "Músculos e Ossos (Kg)"];
  const [isDark, setIsDark] = useState(
    typeof window !== "undefined"
      ? document.body.classList.contains("dark")
      : false,
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      const observer = new MutationObserver(() => {
        setIsDark(document.body.classList.contains("dark"));
      });
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });
      return () => observer.disconnect();
    }
  }, []);

  const _data: Array<number> = [data.fat, data.wastes, data.muscles].map((e) =>
    parseFloat(e.toFixed(2)),
  );

  if (data.bones) {
    _data.push(parseFloat(data.bones.toFixed(2)));
    labels4.push("Ossos (Kg)");
    labels4[2] = "Músculos (Kg)";
    colors4[2] = "#7625F5";
    colors4.push("#FFB800");
  } else {
    labels4[2] = "Músculos e Ossos (Kg)";
  }

  useEffect(() => {
    // Ensure the chart is re-rendered when the data changes
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("resize"));
    }
  }, [isDark]);

  // Set colors based on theme

  return (
    <div className="flex max-w-[300px] flex-col items-center overflow-hidden rounded-lg border border-zinc-200 bg-zinc-300 shadow-inner dark:border-zinc-700 dark:bg-zinc-800">
      <div className="flex w-full items-center justify-center rounded-t-lg bg-zinc-200 py-2 dark:bg-zinc-700">
        <span className="text-base font-semibold text-zinc-700 dark:text-zinc-100">
          Composição Corporal
        </span>
      </div>
      <div className="flex w-full items-center justify-center bg-white py-2 dark:bg-zinc-800">
        <ReactApexChart
          type="donut"
          height={200}
          series={_data}
          labels={labels4}
          options={{
            chart: { type: "donut", background: "transparent" },
            colors: colors4,
            legend: {
              show: false,
              labels: {
                colors: [isDark ? "#f4f4f5" : "#27272a"],
              },
            },
            labels: labels4,
            plotOptions: {
              pie: {
                donut: {
                  size: "60%",
                  background: "transparent",
                  labels: {
                    show: true,
                    total: {
                      show: true,
                      label: "Total",
                      fontSize: "14px",
                      color: isDark ? "#d4d4d8" : "#444",
                      formatter: () =>
                        _data.reduce((a, b) => a + b, 0).toFixed(2),
                    },
                  },
                },
              },
            },
            dataLabels: {
              enabled: false,
            },
            tooltip: {
              theme: isDark ? "dark" : "light",
              y: {
                formatter: (val: number) => `${val.toFixed(2)} Kg`,
              },
            },
            theme: {
              mode: isDark ? "dark" : "light",
            },
          }}
        />
      </div>
      <div className="flex w-full flex-col gap-1 rounded-b-lg bg-zinc-100 px-3 py-2 dark:bg-zinc-700">
        {labels4.map((element, index) => (
          <div
            key={index + "label"}
            className="flex items-center justify-between text-xs text-zinc-700 dark:text-zinc-100"
          >
            <div className="flex items-center">
              <MdCircle color={colors4[index]} size={12} />
              <span className="ml-2" />
              <p className="font-medium uppercase">{element}</p>
            </div>
            <p className="font-bold">{_data[index].toFixed(2)} Kg</p>
          </div>
        ))}
      </div>
    </div>
  );
};

interface AssessmentContentProps {
  id: string;
  handleUnselect(): void;
  handleReload(): void;
  handleDelete(): void;
}

const AssessmentContent: React.FC<AssessmentContentProps> = (props) => {
  const { student, age } = useStudent();
  const [antrop, setAntrop] = useState<AssessmentClass>();

  const button = useLoadingButton(handleSave);

  const { loadAssessment, save, setUnsaved } = useAssessmentContext();

  const duplicate = useDuplicateAssessment(props.id);

  const [values, setValues] = useState<GraphData>({
    fat: 0,
    wastes: 0,
    muscles: 0,
  });

  useEffect(() => {
    if (!antrop) ReloadData(props.id);
  }, [props.id]);

  async function ReloadData(id: string) {
    if (!student) return;

    loadAssessment(id).then((res) => {
      if (res) {
        setAntrop(res);
        setValues(GetAntrophoGraphData(res, student.gender, age));
      }
    });
  }

  async function handleSave() {
    if (!student || !antrop) return;

    save({
      ...antrop,
      graph: GetAntrophoGraphData(antrop, student.gender, age),
    } as AssessmentClass).then(() => {
      setValues(GetAntrophoGraphData(antrop, student.gender, age));
      button.changeState("LOADED");
    });
  }

  function handleDuplicate() {
    duplicate.mutateAsync().then(() => {
      props.handleUnselect();
      props.handleReload();
    });
  }

  function onChangeData() {
    if (!antrop || !student) return;
    button.changeState("UNLOADED");
    setUnsaved();
    setValues(GetAntrophoGraphData(antrop, student.gender, age));
  }

  function handleChangeWeight(value: string) {
    if (!antrop) return;

    const v = isNaN(parseFloat(value)) ? 0 : parseFloat(value);

    setAntrop({ ...antrop, weight: v });

    onChangeData();
  }

  function handleChangeHeight(value: string) {
    if (!antrop) return;

    let v = isNaN(parseFloat(value)) ? 0 : parseFloat(value);

    if (v && v > 10) v = v / Math.pow(10, value.length - 1);

    setAntrop({ ...antrop, height: v });

    onChangeData();
  }

  function handleChangeTitle(e: string) {
    if (antrop) setAntrop({ ...antrop, title: e });

    onChangeData();
  }

  function handleChangeProtocol(protocol: number) {
    if (antrop)
      setAntrop({
        ...antrop,
        content: { ...antrop.content, protocol: protocol },
      });
    onChangeData();
  }

  function handleChangeFolds(folds: SkinFoldsList) {
    if (antrop)
      setAntrop({
        ...antrop,
        content: { ...antrop.content, folds: folds },
      });
    onChangeData();
  }

  function handleChangeBones(bones: BonesList) {
    if (antrop)
      setAntrop({
        ...antrop,
        content: { ...antrop.content, bones: bones },
      });
    onChangeData();
  }

  function handleChangePerimetry(perimetry: PerimetryList) {
    if (antrop)
      setAntrop({
        ...antrop,
        content: { ...antrop.content, perimetry: perimetry },
      });
    onChangeData();
  }

  if (!antrop)
    return (
      <div className="flex flex-1 items-center justify-center">
        <Audio color="black" height={50} width={50} />
      </div>
    );
  else
    return (
      <div className="fade-in flex flex-1 flex-col overflow-hidden p-1">
        <div className="flex justify-between p-2">
          <input
            className="rounded-none bg-transparent text-xl text-zinc-800 underline decoration-zinc-300 shadow-none dark:text-zinc-100"
            placeholder="Titulo da Avaliação..."
            defaultValue={antrop.title}
            onBlur={(e) => handleChangeTitle(e.target.value)}
          />
          <div className="flex items-center">
            <button
              className="flex rounded-full bg-zinc-400 p-1 text-white hover:opacity-80 dark:bg-zinc-700"
              onClick={handleDuplicate}
            >
              <BiCopy size={24} />
            </button>
            <span className="mx-1" />
            <button
              className="flex rounded-full bg-zinc-400 p-1 text-white hover:opacity-80 dark:bg-zinc-700"
              onClick={props.handleDelete}
            >
              <MdDelete size={24} />
            </button>
            {button.Button}
          </div>
        </div>
        <ul className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto overflow-x-hidden">
          <li className="mt-1 flex flex-col flex-wrap items-center rounded bg-zinc-100 p-2 dark:bg-zinc-800">
            <h1 className="mb-2 text-lg font-bold text-zinc-700 dark:text-zinc-100">
              Informações
            </h1>
            <div className="flex w-full max-w-3xl flex-col justify-between gap-4 md:flex-row">
              {/* Left: Image and Student Info */}
              <div className="flex flex-1 flex-row gap-4">
                <div className="relative flex aspect-[0.75] w-[140px] min-w-[100px] max-w-[180px] items-center justify-center rounded border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                  <img
                    src={require("../../../assets/images/human-male.jpg")}
                    className="h-full w-full object-cover opacity-10"
                    alt="Human"
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <div className="mb-2">
                    <h3 className="text-xs text-zinc-500 dark:text-zinc-300">
                      Nome
                    </h3>
                    <p className="text-base font-medium text-zinc-800 dark:text-zinc-100">
                      {student?.name}
                    </p>
                  </div>
                  <div className="mb-2">
                    <h3 className="text-xs text-zinc-500 dark:text-zinc-300">
                      Nascimento
                    </h3>
                    <p className="text-base text-zinc-700 dark:text-zinc-200">
                      {age} anos
                      {student && (
                        <span>, {moment(student.birthdate).format("LL")}</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xs text-zinc-500 dark:text-zinc-300">
                      Sexo Biológico
                    </h3>
                    {!student?.gender ? (
                      <p className="text-red-500">Não Cadastrado!</p>
                    ) : (
                      <p className="text-base text-zinc-700 dark:text-zinc-200">
                        {student?.gender.toLowerCase() === "male"
                          ? "Masculino"
                          : "Feminino"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {/* Right: Donut Chart */}
              <div className="flex min-w-[160px] flex-col items-center justify-center">
                <DonutChart data={values} />
              </div>
            </div>
          </li>
          <li className="mt-1 flex flex-col flex-wrap items-center rounded bg-zinc-100 p-2 dark:bg-zinc-800">
            <h1 className="mb-2 text-lg font-bold text-zinc-700 dark:text-zinc-100">
              Estatura e Peso
            </h1>
            <div className="flex flex-col">
              <div className="flex w-[150px] items-center justify-between">
                <Inputfield
                  inputProps={{
                    placeholder: "Estatura",
                    defaultValue: antrop.height,
                    type: "number",
                    max: 999,
                    maxLength: 5,
                    step: 0.01,
                    style: {
                      textAlign: "right",
                    },
                  }}
                  width={120}
                  icon={MdHeight}
                  onChangeText={handleChangeHeight}
                  selectOnFocus
                  hint="Estatura (M/Cm)"
                />
              </div>
              <span />
              <div className="flex w-[150px] items-center justify-between">
                <Inputfield
                  inputProps={{
                    placeholder: "Peso",
                    defaultValue: antrop.weight,
                    type: "number",
                    step: 0.1,
                    style: {
                      textAlign: "right",
                    },
                    maxLength: 5,
                  }}
                  width={120}
                  icon={MdMonitorWeight}
                  onChangeText={handleChangeWeight}
                  selectOnFocus
                  hint=" Peso (Kg)"
                />
              </div>
            </div>
          </li>
          <li className="mt-1 flex flex-col flex-wrap items-center rounded bg-zinc-100 p-2 dark:bg-zinc-800">
            <h1 className="mb-2 text-lg font-bold text-zinc-700 dark:text-zinc-100">
              Circunferências
            </h1>
            <Perimetry
              protocol={antrop.content.protocol}
              defaultPerimetry={antrop.content.perimetry}
              onChangePerimetry={handleChangePerimetry}
            />
          </li>
          <li className="mt-1 flex flex-col flex-wrap items-center rounded bg-zinc-100 p-2 dark:bg-zinc-800">
            <h1 className="mb-2 text-lg font-bold text-zinc-700 dark:text-zinc-100">
              Dobras Cutâneas
            </h1>
            <Folds
              defaultProtocol={antrop.content.protocol}
              defaultValues={antrop.content.folds}
              onChangeProtocol={handleChangeProtocol}
              onChangeFolds={handleChangeFolds}
            />
          </li>
          <li className="mt-1 flex flex-col flex-wrap items-center rounded bg-zinc-100 p-2 dark:bg-zinc-800">
            <h1 className="mb-2 text-lg font-bold text-zinc-700 dark:text-zinc-100">
              Diâmetro Ósseo
            </h1>
            <Bones
              onChangeBones={handleChangeBones}
              defaultBones={antrop.content.bones}
            />
          </li>
        </ul>
      </div>
    );
};

export default AssessmentContent;
